/**
 * Route → screen key in GrECo Help Admin, the catalog behind the user guide (`<greco-help>`).
 *
 * The keys are what Help Admin stores the help under, so they never change once help is written
 * for them; a new page gets a new key, and its screen and help are added in Help Admin
 * (`screens.test.ts` fails on a routed page without one).
 */
export const HELP_ADMIN_SCREENS: Record<string, string> = {
  '/': 'dashboard',
  '/projects': 'projects',
  '/projects/:code/keys': 'project-keys',
  '/projects/:code/linked-keys': 'project-linked-keys',
  '/projects/:code/suggestions': 'project-suggestions',
  '/projects/:code/settings': 'project-settings',
  '/projects/:code/releases': 'project-releases',
  '/projects/:code/missing': 'project-missing-keys',
  '/projects/:code/imports': 'project-imports',
  '/projects/:code/activity': 'project-activity',
  '/packages': 'packages',
  '/packages/:code/keys': 'package-keys',
  '/packages/:code/imports': 'package-imports',
  '/packages/:code/settings': 'package-settings',
  '/packages/:code/suggestions': 'package-suggestions',
  '/glossary': 'glossary',
  '/search': 'search',
  '/settings/users': 'settings-users',
  '/settings/api-tokens': 'settings-api-tokens',
  '/settings/locales': 'settings-locales',
  '/settings/publish-targets': 'settings-publish-targets',
  '/settings/phrase-sync': 'settings-phrase-sync',
};

/** The navigation, project switcher, key search, bell and user menu: on every page, documented once. */
export const SHELL_SCREEN = 'shell';

/** Segment-wise match, where `:param` accepts any single non-empty segment. */
export const matchesRoute = (route: string, pathname: string): boolean => {
  const pattern = route.split('/');
  const actual = pathname.split('/');
  if (pattern.length !== actual.length) return false;
  return pattern.every((segment, i) =>
    segment.startsWith(':') ? (actual[i] ?? '').length > 0 : segment === actual[i],
  );
};

/**
 * The route that answers for a path: an exact route first, then a `:param` pattern. No prefix
 * fallback: `/packages` must not answer for `/packages/x/keys`, or the guide would list controls
 * that are not on the screen.
 */
export const helpRouteForPath = (pathname: string): string | null => {
  if (HELP_ADMIN_SCREENS[pathname]) return pathname;
  return (
    Object.keys(HELP_ADMIN_SCREENS).find(
      (route) => route.includes('/:') && matchesRoute(route, pathname),
    ) ?? null
  );
};

/**
 * The `data-help-screen` value for a path: the page's own screen first (it names the drawer),
 * then the shell's. A page without a registered screen still lists the shell.
 */
export const helpScreensForPath = (pathname: string): string => {
  const route = helpRouteForPath(pathname);
  const page = route ? HELP_ADMIN_SCREENS[route] : undefined;
  return page ? `${page} ${SHELL_SCREEN}` : SHELL_SCREEN;
};
